// MatkaPanelChart — the old khelpur `doublechart.php` / `singlechart.php`, as ONE component
// (owner, 2026-09-20: "a proper chart, not any rubbish chart … panel chart with a toggle to
// the jodi chart, where only the pattis disappear"). Used inside the play sheet (compact,
// the last 5–6 weeks) and on the Results tab (full history). Pure display: give it the
// /chart payload and a mode.
//
// Double game = weeks down, Mon–Sun across. Every cell reads like the old one:
//     open patti digits stacked │ JODI (big) │ close patti digits stacked
// Jodi mode hides the two patti columns and nothing else. Red = the jodi the old board
// coloured red (a double like 11/22 or a "cut" pair 05/16/27/38/49/50/61/72/83/94), plus
// whatever the server flagged. Today's cell is tinted. The week column shows the date
// range and the count of distinct figures that came up that week (the old badge), and the
// strip under the table lists this week's figures that have NOT come yet (the old
// "Remaining numbers of this week").
//
// Single game = days down (newest first), the slot times across; cell = patti over digit.
// Jodi mode shows the digit only.
import { useEffect, useMemo, useRef } from 'react';

export type ChartDay = { date: string; openPana?: string | null; openDigit?: string | null; closePana?: string | null; closeDigit?: string | null; jodi?: string | null; red?: boolean; holiday?: boolean };
export type ChartWeek = { monday: string; days: ChartDay[] };
// One shape for both kinds (the discriminant sits in game.kind, which TS cannot narrow on):
// a double game fills `weeks`, a single game fills `slots` + `days`.
export type ChartData = {
  game: { id: number; name: string; kind: 'double' | 'single' };
  from?: string; to?: string; weeks?: ChartWeek[];
  slots?: Array<{ slot: number; time: string }>;
  days?: Array<{ date: string; holiday?: boolean; slots: Array<{ slot: number; pana?: string | null; digit?: string | null }> }>;
};
export type ChartMode = 'panel' | 'jodi';

const DANGER = new Set(['05', '16', '27', '38', '49', '50', '61', '72', '83', '94']);
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const ddmm = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}`;
const hhmm = (t?: string | null) => { if (!t) return ''; const [h, m] = t.split(':').map(Number); return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')}${h >= 12 ? 'pm' : 'am'}`; };
const isRed = (d: ChartDay) => !!d.red || (!!d.jodi && (DANGER.has(d.jodi) || d.jodi[0] === d.jodi[1]));

function Patti({ p, faded }: { p?: string | null; faded?: boolean }) {
  const chars = p && p.length === 3 ? p.split('') : ['-', '-', '-'];
  return <span className={`flex flex-col items-center leading-[1.02] font-mono text-[9px] sm:text-[10px] ${faded ? 'opacity-40' : 'opacity-90'}`}>{chars.map((c, i) => <span key={i}>{c}</span>)}</span>;
}

export function ChartModeToggle({ mode, onMode, tone = 'amber' }: { mode: ChartMode; onMode: (m: ChartMode) => void; tone?: 'amber' | 'sky' }) {
  const on = tone === 'amber' ? 'bg-amber-600 text-white' : 'bg-sky-600 text-white';
  return (
    <div className="inline-flex rounded-lg bg-arena-100 dark:bg-white/10 p-0.5 text-[11px] font-bold">
      <button type="button" onClick={() => onMode('panel')} className={`px-2 py-0.5 rounded-md ${mode === 'panel' ? on : ''}`}>Panel</button>
      <button type="button" onClick={() => onMode('jodi')} className={`px-2 py-0.5 rounded-md ${mode === 'jodi' ? on : ''}`}>Jodi</button>
    </div>
  );
}

export default function MatkaPanelChart({ data, mode, today, weeks: weekLimit, compact = false, scrollToLatest = true }: {
  data: ChartData | null; mode: ChartMode; today: string; weeks?: number; compact?: boolean; scrollToLatest?: boolean;
}) {
  const boxRef = useRef<HTMLDivElement>(null);
  const weeks = useMemo(() => {
    if (!data || data.game.kind !== 'double') return [];
    const all = (data.weeks || []).filter((w) => w.days.length);
    return weekLimit ? all.slice(-weekLimit) : all;
  }, [data, weekLimit]);
  // The newest week is the LAST row (the old chart runs oldest → newest); start scrolled to it.
  useEffect(() => { if (scrollToLatest && boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight; }, [weeks.length, scrollToLatest, data]);

  if (!data) return <div className={`text-center text-arena-500 ${compact ? 'text-[11px] py-3' : 'text-sm py-8'}`}>Loading chart…</div>;

  if (data.game.kind === 'single') {
    const allDays = data.days || [];
    const days = compact ? allDays.slice(0, 7) : allDays;
    const slots = data.slots || [];
    return (
      <div ref={boxRef} className={`overflow-auto ${compact ? 'max-h-[30dvh]' : ''}`}>
        <table className="min-w-full border-collapse text-center">
          <thead className="sticky top-0 z-10">
            <tr className="bg-black/60 text-white text-[9px] sm:text-[10px] font-bold">
              <th className="px-1 py-1 text-left whitespace-nowrap">Date</th>
              {slots.map((s) => <th key={s.slot} className="px-1 py-1 whitespace-nowrap">{s.slot}<span className="block font-normal opacity-80">{hhmm(s.time)}</span></th>)}
            </tr>
          </thead>
          <tbody>
            {days.map((d) => (
              <tr key={d.date} className={`border-t border-arena-100 dark:border-white/10 ${d.date === today ? 'bg-amber-100/70 dark:bg-amber-500/15' : ''}`}>
                <td className="px-1 py-0.5 text-left text-[10px] font-bold whitespace-nowrap">{ddmm(d.date)}{d.date === today ? <span className="ml-1 text-[8px] uppercase text-amber-700 dark:text-amber-300">today</span> : null}</td>
                {slots.map((s) => {
                  const r = d.slots.find((x) => x.slot === s.slot);
                  return (
                    <td key={s.slot} className="px-0.5 py-0.5">
                      {d.holiday ? <span className="text-arena-300">**</span> : (
                        <div className="flex flex-col items-center">
                          {mode === 'panel' && <span className="font-mono text-[9px] opacity-80">{r?.pana ?? '---'}</span>}
                          <span className="font-black text-[15px] leading-none">{r?.digit ?? '-'}</span>
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
            {!days.length && <tr><td colSpan={slots.length + 1} className="py-4 text-[11px] text-arena-500">No results yet.</td></tr>}
          </tbody>
        </table>
      </div>
    );
  }

  const thisWeek = weeks.find((w) => w.days.some((d) => d.date === today));
  const remaining = thisWeek
    ? ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'].filter((f) => !thisWeek.days.some((d) => d.openDigit === f || d.closeDigit === f))
    : [];
  return (
    <div>
      <div ref={boxRef} className={`overflow-auto rounded-lg ${compact ? 'max-h-[30dvh]' : ''}`}>
        <table className="w-full border-collapse text-center table-fixed">
          <thead className="sticky top-0 z-10">
            <tr className="bg-black/60 text-white text-[9px] sm:text-[10px] font-bold">
              <th className="px-0.5 py-1 w-[15%]">Date</th>
              {DAYS.map((d, i) => <th key={d} className={`px-0 py-1 ${thisWeek && thisWeek.days[i]?.date === today ? 'text-amber-300' : ''}`}>{d}</th>)}
            </tr>
          </thead>
          <tbody>
            {weeks.map((w) => {
              const first = w.days[0]?.date, last = w.days[w.days.length - 1]?.date;
              const figures = new Set<string>();
              for (const d of w.days) { if (d.openDigit) figures.add(d.openDigit); if (d.closeDigit) figures.add(d.closeDigit); }
              const current = w === thisWeek;
              return (
                <tr key={w.monday} className="border-t border-arena-100 dark:border-white/10">
                  <td className="px-0.5 py-0.5 align-middle">
                    <div className={`rounded-md py-0.5 text-[8px] sm:text-[9px] leading-tight font-bold ${current ? 'bg-amber-600 text-white' : 'text-arena-600 dark:text-white/70'}`}>
                      <div>{first ? ddmm(first) : ''}</div>
                      <div className={`border-t ${current ? 'border-white/40' : 'border-arena-200 dark:border-white/15'}`}>{last ? ddmm(last) : ''}</div>
                    </div>
                    {figures.size > 0 && <div className="mt-0.5"><span className="inline-block rounded bg-sky-600 text-white text-[8px] font-black px-1 leading-tight" title="Different figures that came this week">{figures.size}</span></div>}
                  </td>
                  {w.days.map((d) => {
                    const isToday = d.date === today;
                    const future = d.date > today;
                    const red = isRed(d);
                    const has = !!(d.openDigit || d.closeDigit);
                    return (
                      <td key={d.date} className="px-0 py-0.5 align-middle">
                        <div className={`mx-auto flex items-center justify-center gap-[2px] rounded-md py-0.5 ${isToday ? 'bg-amber-200 dark:bg-amber-500/30' : ''} ${red ? 'text-red-600 dark:text-red-400' : ''}`}>
                          {d.holiday ? <span className="text-arena-300 text-[10px]">**</span> : future ? <span className="opacity-0">.</span> : (
                            <>
                              {mode === 'panel' && <Patti p={d.openPana} faded={!d.openPana} />}
                              <span className={`font-black leading-none ${compact ? 'text-[15px]' : 'text-[19px] sm:text-[22px]'} ${has ? '' : 'opacity-30'}`}>{d.openDigit ?? '*'}{d.closeDigit ?? '*'}</span>
                              {mode === 'panel' && <Patti p={d.closePana} faded={!d.closePana} />}
                            </>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            {!weeks.length && <tr><td colSpan={8} className="py-4 text-[11px] text-arena-500">No results yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {thisWeek && (
        <div className="mt-1 rounded-md bg-sky-600/80 text-white text-center py-0.5 px-1 text-[10px] font-bold">
          {remaining.length ? <>This week's figures still to come: <span className="tracking-[0.2em] font-black">{remaining.join(' ')}</span></> : 'Every figure has come this week'}
        </div>
      )}
    </div>
  );
}
